// handleApiError.ts (Updated to return statusCode)

import { ERROR_TYPE } from "app/types";
import toastService from "utils/toast";

/**
 * ... (Function documentation remains the same)
 *
 * New Return Structure:
 * {
 * type: ERROR_TYPE | string,
 * message: string,
 * statusCode?: number, // <-- NEW
 * errors?: any[]
 * }
 */

const ignoreMessages = [
  `Cannot read properties of undefined (reading 'fileId')`,
  `Content not exist error.`,
  `e is undefined`,
  "call: argument of type {context, fn} has undefined or null `fn`",
  "mime.getType is not a function",
  `undefined is not an object (evaluating 'e.context')`,
  `Cannot read properties of undefined (reading 'context')`,
  `Cannot read properties of undefined (reading '0')`,
  `call: argument fn is undefined or null`,
  `Cannot read properties of undefined (reading 'results')`,
  `Cannot set properties of undefined (setting 'ticketList')`,
  `Cannot read properties of undefined (reading 'page')`,
];
export function parsedErrorMessage(error: any) {
  // Axios often places the API response body in error.response.data
  let parsedError = error.data || error.response?.data || error;

  // If backend sends stringified JSON inside error.message (ResponseError)
  if (
    typeof parsedError.message === "string" &&
    parsedError.message.startsWith("{")
  ) {
    try {
      parsedError = JSON.parse(parsedError.message);
    } catch {
      // If parsing fails, fall back to the original parsedError
    }
  }
  return parsedError;
}

export function handleApiError(
  error: any,
  options?: { showToast?: boolean; returnParsed?: boolean }
) {
  const shouldShowToast = options?.showToast !== false;

  if (!error) {
    if (shouldShowToast)
      toastService.error("Something went wrong. Please try again.");
    return {
      type: ERROR_TYPE.DEFAULT,
      message: "Something went wrong. Please try again.",
    };
  }

  // 💡 FIX 1: Extract statusCode and response data early
  const originalStatus = error.status || error.response?.status;

  // Axios often places the API response body in error.response.data
  let parsedError = parsedErrorMessage(error);

  // Determine the final status code from API body or Axios response
  const finalStatusCode = parsedError.statusCode || originalStatus;

  // --- Case 2: Validation errors (with `errors` array) ---
  if (Array.isArray(parsedError.errors)) {
    const errors = parsedError.errors.map((err: any) => ({
      type: err?.type || ERROR_TYPE.DEFAULT,
      message: err?.message || "Validation error occurred.",
    }));

    if (shouldShowToast) {
      errors.forEach((e) => toastService.error(e.message));
    }

    return {
      type: errors[0]?.type || ERROR_TYPE.DEFAULT,
      message: errors[0]?.message || "Validation error occurred.",
      statusCode: finalStatusCode, // <-- ADDED
      errors,
    };
  }
  // --- Case 3: Single typed error (or your common API error format) ---
  if (parsedError.type || parsedError.message || finalStatusCode) {
    const normalized = {
      // Use API message, or axios message, or a fallback
      message:
        parsedError.message || error.message || "Unexpected error occurred.",
      type: parsedError.type || ERROR_TYPE.DEFAULT,
      statusCode: finalStatusCode, // <-- ADDED
    };
    if (ignoreMessages.includes(normalized.message)) {
    } else {
      if (shouldShowToast) toastService.error(normalized.message);
    }

    return normalized;
  }
  // Cannot read properties of undefined (reading 'context')

  // --- Case 4: Unknown structure fallback ---
  if (shouldShowToast) toastService.error("Unexpected error occurred.");
  return {
    type: ERROR_TYPE.DEFAULT,
    message: "Unexpected error occurred.",
    statusCode: finalStatusCode, // <-- ADDED
  };
}

/**
 * Handles authentication-related errors (403, 404) by showing a toast and redirecting to home.
 * For other errors, delegates to the default handleApiError.
 */
export function handleAuthError(err: any) {
  const parsed = handleApiError(err, {
    showToast: false,
    returnParsed: true,
  });
  switch (parsed?.statusCode) {
    case 403:
    case 404:
      toastService.error(parsed.message);
      setTimeout(() => {
        window.location.href = '/';
      }, 1000);
      break;
    default:
      handleApiError(err);
      break;
  }
}
